import React, { useRef, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Sphere, Html, Trail } from '@react-three/drei';

// Orbital Ring (visible path)
const OrbitalRing = ({ radius, color = "#94A3B8" }) => {
    return (
        <mesh rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[radius, 0.03, 16, 100]} />
            <meshStandardMaterial color={color} opacity={0.5} transparent />
        </mesh>
    );
};

// Moving Electron with trail - orbits in XZ plane, parent group handles rotation
const Electron = ({ radius, speed, offset, color = "#3B82F6" }) => {
    const ref = useRef();

    useFrame((state) => {
        const t = state.clock.getElapsedTime() * speed + offset;
        ref.current.position.x = Math.cos(t) * radius;
        ref.current.position.y = 0;
        ref.current.position.z = Math.sin(t) * radius;
    });

    return (
        <Trail width={2} length={15} color={color} attenuation={(t) => t * t}>
            <mesh ref={ref}>
                <sphereGeometry args={[0.25, 16, 16]} />
                <meshStandardMaterial
                    color={color}
                    emissive={color}
                    emissiveIntensity={0.8}
                />
            </mesh>
        </Trail>
    );
};

const Nucleus = () => {
    const particles = [
        { pos: [0.2, 0.2, 0.2], color: "#EF4444" },
        { pos: [-0.2, -0.2, -0.2], color: "#EF4444" },
        { pos: [0.2, -0.2, 0.1], color: "#94A3B8" },
        { pos: [-0.2, 0.2, -0.1], color: "#94A3B8" },
        { pos: [0, 0, 0], color: "#EF4444" },
    ];

    return (
        <group>
            {particles.map((p, i) => (
                <mesh key={i} position={p.pos}>
                    <sphereGeometry args={[0.28, 32, 32]} />
                    <meshStandardMaterial color={p.color} roughness={0.3} metalness={0.4} />
                </mesh>
            ))}
            <Html distanceFactor={10}>
                <div className="pointer-events-none text-xs font-bold text-slate-700 bg-white/90 px-2 py-1 rounded-full whitespace-nowrap backdrop-blur-sm -translate-y-8">
                    Nucleus
                </div>
            </Html>
        </group>
    );
};

export const AtomStructure = () => {
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
            <h4 className="text-lg font-bold text-slate-800 mb-2 text-center">Interactive: Atomic Structure (3D)</h4>
            <p className="text-xs text-slate-600 text-center mb-4">
                Bohr Model - Watch electrons orbit the nucleus
            </p>
            <div className="h-[400px] w-full bg-slate-900/5 rounded-xl overflow-hidden relative border border-slate-200 shadow-inner">
                <Canvas camera={{ position: [0, 4, 12], fov: 45 }}>
                    <ambientLight intensity={0.6} />
                    <pointLight position={[10, 10, 10]} intensity={1.5} />
                    <pointLight position={[-10, -10, -10]} intensity={0.8} />

                    <Nucleus />

                    {/* Shell 1 (K shell) - 2 electrons */}
                    <OrbitalRing radius={2.5} color="#60A5FA" />
                    <Electron radius={2.5} speed={1.2} offset={0} color="#60A5FA" />
                    <Electron radius={2.5} speed={1.2} offset={Math.PI} color="#60A5FA" />

                    {/* Shell 2 (L shell) - 4 electrons */}
                    <OrbitalRing radius={4.5} color="#3B82F6" />
                    <Electron radius={4.5} speed={0.8} offset={0} color="#3B82F6" />
                    <Electron radius={4.5} speed={0.8} offset={Math.PI / 2} color="#3B82F6" />
                    <Electron radius={4.5} speed={0.8} offset={Math.PI} color="#3B82F6" />
                    <Electron radius={4.5} speed={0.8} offset={3 * Math.PI / 2} color="#3B82F6" />

                    {/* Shell 3 (M shell) - 6 electrons */}
                    <OrbitalRing radius={6.5} color="#2563EB" />
                    <Electron radius={6.5} speed={0.5} offset={0} color="#2563EB" />
                    <Electron radius={6.5} speed={0.5} offset={Math.PI / 3} color="#2563EB" />
                    <Electron radius={6.5} speed={0.5} offset={2 * Math.PI / 3} color="#1D4ED8" />
                    <Electron radius={6.5} speed={0.5} offset={Math.PI} color="#1D4ED8" />
                    <Electron radius={6.5} speed={0.5} offset={4 * Math.PI / 3} color="#2563EB" />
                    <Electron radius={6.5} speed={0.5} offset={5 * Math.PI / 3} color="#1D4ED8" />

                    <OrbitControls
                        enableZoom={true}
                        minDistance={6}
                        maxDistance={25}
                        autoRotate
                        autoRotateSpeed={0.8}
                        enablePan={false}
                    />
                </Canvas>

                <div className="absolute bottom-2 right-2 text-[10px] text-slate-400 font-medium">
                    Drag to rotate • Scroll to zoom
                </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-4 text-center text-sm">
                <div className="p-3 bg-white rounded-lg shadow-sm flex flex-col items-center justify-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-red-500 shadow-sm ring-2 ring-red-100"></div>
                    <span className="font-medium text-slate-700">Protons (+)</span>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm flex flex-col items-center justify-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-slate-400 shadow-sm ring-2 ring-slate-100"></div>
                    <span className="font-medium text-slate-700">Neutrons (0)</span>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm flex flex-col items-center justify-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-blue-500 shadow-sm ring-2 ring-blue-100"></div>
                    <span className="font-medium text-slate-700">Electrons (−)</span>
                </div>
            </div>

            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-slate-700 leading-relaxed">
                    <strong className="text-amber-700">Note:</strong> This is the <em>Bohr Model</em> (simplified).
                    Real electrons exist as probability clouds, but this model helps visualize atomic structure!
                </p>
            </div>
        </div>
    );
};

/**
 * Quark Types Visualization
 * Shows all 6 quark flavors with properties
 */
export const QuarkTable = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;

        // Clear
        ctx.fillStyle = '#FAFAFA';
        ctx.fillRect(0, 0, width, height);

        const quarks = [
            { name: 'Up', symbol: 'u', charge: '+2/3', mass: '2.2 MeV', color: '#22C55E', gen: 1 },
            { name: 'Down', symbol: 'd', charge: '-1/3', mass: '4.7 MeV', color: '#3B82F6', gen: 1 },
            { name: 'Charm', symbol: 'c', charge: '+2/3', mass: '1.27 GeV', color: '#F59E0B', gen: 2 },
            { name: 'Strange', symbol: 's', charge: '-1/3', mass: '95 MeV', color: '#8B5CF6', gen: 2 },
            { name: 'Top', symbol: 't', charge: '+2/3', mass: '173 GeV', color: '#EF4444', gen: 3 },
            { name: 'Bottom', symbol: 'b', charge: '-1/3', mass: '4.18 GeV', color: '#EC4899', gen: 3 },
        ];

        // Title
        ctx.fillStyle = '#1E293B';
        ctx.font = 'bold 16px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('The Six Quark Flavors', width / 2, 30);

        // Generation labels
        ctx.font = '12px Inter, sans-serif';
        ctx.fillStyle = '#64748B';
        ['Generation I', 'Generation II', 'Generation III'].forEach((label, i) => {
            ctx.fillText(label, 85 + i * 150, 55);
        });

        // Draw quark cards
        quarks.forEach((quark, i) => {
            const col = quark.gen - 1;
            const row = quark.charge === '+2/3' ? 0 : 1;
            const x = 20 + col * 150;
            const y = 70 + row * 110;

            // Card background
            ctx.fillStyle = 'white';
            ctx.shadowColor = 'rgba(0,0,0,0.1)';
            ctx.shadowBlur = 10;
            ctx.shadowOffsetY = 2;
            ctx.beginPath();
            ctx.roundRect(x, y, 130, 95, 12);
            ctx.fill();
            ctx.shadowBlur = 0;

            // Colored accent
            ctx.fillStyle = quark.color;
            ctx.beginPath();
            ctx.roundRect(x, y, 130, 6, [12, 12, 0, 0]);
            ctx.fill();

            // Symbol circle
            ctx.beginPath();
            ctx.arc(x + 35, y + 45, 22, 0, Math.PI * 2);
            ctx.fillStyle = quark.color + '20';
            ctx.fill();
            ctx.fillStyle = quark.color;
            ctx.font = 'bold 24px Georgia, serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(quark.symbol, x + 35, y + 45);

            // Name and properties
            ctx.textAlign = 'left';
            ctx.fillStyle = '#1E293B';
            ctx.font = 'bold 14px Inter, sans-serif';
            ctx.fillText(quark.name, x + 65, y + 35);

            ctx.fillStyle = '#64748B';
            ctx.font = '11px Inter, sans-serif';
            ctx.fillText(`Q: ${quark.charge}`, x + 65, y + 52);
            ctx.fillText(`m: ${quark.mass}`, x + 65, y + 67);
        });

    }, []);

    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-green-50 rounded-2xl border border-slate-200">
            <canvas
                ref={canvasRef}
                width={480}
                height={290}
                className="mx-auto block rounded-xl"
            />
            <p className="text-center text-sm text-slate-500 mt-4">
                Quarks combine in groups of 2 (mesons) or 3 (baryons) to form hadrons
            </p>
        </div>
    );
};

/**
 * Standard Model Overview
 * Interactive particle family tree
 */
export const StandardModelDiagram = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;

        ctx.fillStyle = '#FAFAFA';
        ctx.fillRect(0, 0, width, height);

        // Section backgrounds
        const sections = [
            { x: 10, y: 50, w: 180, h: 200, label: 'QUARKS', color: '#22C55E20', borderColor: '#22C55E' },
            { x: 200, y: 50, w: 180, h: 200, label: 'LEPTONS', color: '#3B82F620', borderColor: '#3B82F6' },
            { x: 390, y: 50, w: 90, h: 200, label: 'BOSONS', color: '#F59E0B20', borderColor: '#F59E0B' },
            { x: 490, y: 50, w: 60, h: 200, label: 'HIGGS', color: '#EC489920', borderColor: '#EC4899' },
        ];

        sections.forEach(sec => {
            ctx.fillStyle = sec.color;
            ctx.beginPath();
            ctx.roundRect(sec.x, sec.y, sec.w, sec.h, 8);
            ctx.fill();
            ctx.strokeStyle = sec.borderColor;
            ctx.lineWidth = 2;
            ctx.stroke();

            ctx.fillStyle = sec.borderColor;
            ctx.font = 'bold 10px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(sec.label, sec.x + sec.w / 2, sec.y + 18);
        });

        // Title
        ctx.fillStyle = '#1E293B';
        ctx.font = 'bold 18px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('The Standard Model of Particle Physics', width / 2, 30);

        // Particles
        const drawParticle = (x, y, symbol, name, color) => {
            ctx.beginPath();
            ctx.arc(x, y, 18, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();
            ctx.fillStyle = 'white';
            ctx.font = 'bold 14px Georgia, serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(symbol, x, y);
            ctx.fillStyle = '#475569';
            ctx.font = '9px Inter, sans-serif';
            ctx.fillText(name, x, y + 28);
        };

        // Quarks
        drawParticle(55, 95, 'u', 'up', '#22C55E');
        drawParticle(100, 95, 'c', 'charm', '#16A34A');
        drawParticle(145, 95, 't', 'top', '#15803D');
        drawParticle(55, 165, 'd', 'down', '#3B82F6');
        drawParticle(100, 165, 's', 'strange', '#2563EB');
        drawParticle(145, 165, 'b', 'bottom', '#1D4ED8');

        // Leptons
        drawParticle(245, 95, 'e', 'electron', '#8B5CF6');
        drawParticle(290, 95, 'μ', 'muon', '#7C3AED');
        drawParticle(335, 95, 'τ', 'tau', '#6D28D9');
        drawParticle(245, 165, 'νₑ', 'e neutrino', '#A78BFA');
        drawParticle(290, 165, 'νμ', 'μ neutrino', '#8B5CF6');
        drawParticle(335, 165, 'ντ', 'τ neutrino', '#7C3AED');

        // Bosons
        drawParticle(435, 90, 'γ', 'photon', '#F59E0B');
        drawParticle(435, 140, 'g', 'gluon', '#D97706');
        drawParticle(435, 190, 'Z', 'Z boson', '#B45309');
        drawParticle(435, 230, 'W', 'W boson', '#92400E');

        // Higgs
        drawParticle(520, 140, 'H', 'Higgs', '#EC4899');

    }, []);

    return (
        <div className="my-8 p-6 bg-white rounded-2xl border border-slate-200 shadow-sm">
            <canvas
                ref={canvasRef}
                width={560}
                height={270}
                className="mx-auto block"
            />
        </div>
    );
};

/**
 * Force Carriers Diagram
 */
export const ForceCarriers = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, width, height);

        const forces = [
            {
                name: 'Electromagnetic',
                carrier: 'Photon (γ)',
                strength: 'Strong',
                range: 'Infinite',
                color: '#F59E0B',
                affects: 'Charged particles'
            },
            {
                name: 'Strong Nuclear',
                carrier: 'Gluon (g)',
                strength: 'Strongest',
                range: '10⁻¹⁵ m',
                color: '#22C55E',
                affects: 'Quarks'
            },
            {
                name: 'Weak Nuclear',
                carrier: 'W±, Z⁰',
                strength: 'Weak',
                range: '10⁻¹⁸ m',
                color: '#3B82F6',
                affects: 'All fermions'
            },
            {
                name: 'Gravity',
                carrier: 'Graviton (?)',
                strength: 'Weakest',
                range: 'Infinite',
                color: '#8B5CF6',
                affects: 'All mass/energy'
            },
        ];

        ctx.fillStyle = '#1E293B';
        ctx.font = 'bold 16px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('The Four Fundamental Forces', width / 2, 25);

        forces.forEach((force, i) => {
            const y = 50 + i * 65;

            // Card
            ctx.fillStyle = force.color + '15';
            ctx.beginPath();
            ctx.roundRect(15, y, width - 30, 55, 10);
            ctx.fill();
            ctx.strokeStyle = force.color;
            ctx.lineWidth = 2;
            ctx.stroke();

            // Color bar
            ctx.fillStyle = force.color;
            ctx.beginPath();
            ctx.roundRect(15, y, 6, 55, [10, 0, 0, 10]);
            ctx.fill();

            // Text
            ctx.textAlign = 'left';
            ctx.fillStyle = '#1E293B';
            ctx.font = 'bold 13px Inter, sans-serif';
            ctx.fillText(force.name, 35, y + 20);

            ctx.fillStyle = '#64748B';
            ctx.font = '11px Inter, sans-serif';
            ctx.fillText(`Carrier: ${force.carrier}`, 35, y + 38);

            ctx.textAlign = 'right';
            ctx.fillStyle = force.color;
            ctx.font = 'bold 11px Inter, sans-serif';
            ctx.fillText(force.strength, width - 25, y + 20);
            ctx.fillStyle = '#64748B';
            ctx.font = '10px Inter, sans-serif';
            ctx.fillText(`Range: ${force.range}`, width - 25, y + 38);
        });

    }, []);

    return (
        <div className="my-8">
            <canvas
                ref={canvasRef}
                width={500}
                height={320}
                className="mx-auto block rounded-2xl border border-slate-200 shadow-sm"
            />
        </div>
    );
};

export default {
    AtomStructure,
    QuarkTable,
    StandardModelDiagram,
    ForceCarriers
};
